import graphql from "babel-plugin-relay/macro";
import { useMemo } from "react";
import { useFragment } from "react-relay";

import { useFacilityCourseNoun } from "@/features/Facility/hooks/useFacilityCourseNoun";

import type {
  useEventLocationLabel_Event$data,
  useEventLocationLabel_Event$key,
} from "./__generated__/useEventLocationLabel_Event.graphql";

const useEventLocationLabelFragment = graphql`
  fragment useEventLocationLabel_Event on EventSession {
    courses {
      edges {
        node {
          name
          courseBucket {
            name
          }
        }
      }
    }
    event {
      eventSeries {
        facility {
          ...useFacilityCourseNoun_facility
        }
      }
    }
  }
`;

const getCourseLabel = (
  eventSession: useEventLocationLabel_Event$data,
  pluralCopy: string | undefined | null
): string => {
  const courseEdges =
    eventSession.courses?.edges?.filter(
      (edge): edge is NonNullable<typeof edge> => !!edge?.node
    ) ?? [];

  const courseNames = new Set<string>();
  const courseBucketNames = new Set<string>();

  courseEdges.forEach((courseEdge) => {
    const courseName = courseEdge.node?.name?.trim();
    if (courseName) {
      courseNames.add(courseName);
    }

    const bucketName = courseEdge.node?.courseBucket?.name?.trim();
    if (bucketName) {
      courseBucketNames.add(bucketName);
    }
  });

  if (courseNames.size === 0) {
    return "";
  }

  const shouldIncludeBucket = courseBucketNames.size === 1;
  const courseBucketLabel = shouldIncludeBucket
    ? Array.from(courseBucketNames)[0]
    : undefined;

  const labelForMultipleCourses = `${courseNames.size} ${
    pluralCopy || "Courses"
  }`;

  const courseLabel =
    courseNames.size > 1 ? labelForMultipleCourses : Array.from(courseNames)[0];

  if (courseBucketLabel) {
    return `${courseBucketLabel}: ${courseLabel}`;
  }

  return courseLabel;
};

export const buildEventLocationLabel = (
  eventSession: useEventLocationLabel_Event$data | null | undefined,
  pluralCopy: string | undefined | null
): string | undefined => {
  if (!eventSession) {
    return undefined;
  }

  const label = getCourseLabel(eventSession, pluralCopy);
  return label || undefined;
};

type UseEventLocationLabelArgs = {
  eventSessionRef: useEventLocationLabel_Event$key | null | undefined;
};

export const useEventLocationLabel = ({
  eventSessionRef,
}: UseEventLocationLabelArgs): string | undefined => {
  const eventSession = useFragment(
    useEventLocationLabelFragment,
    eventSessionRef
  );

  const { plural: pluralCopy } = useFacilityCourseNoun(
    eventSession?.event?.eventSeries?.facility ?? null
  );

  return useMemo(
    () => buildEventLocationLabel(eventSession, pluralCopy),
    [eventSession, pluralCopy]
  );
};
